import * as grok from 'datagrok-api/grok';
import * as DG from 'datagrok-api/dg';
/** Custom event id for toggling monomer highlights in macromolecule grid cells (HELM renderer
 *  and generic sequence renderer). Payload: {@link MacromoleculeHighlightEventArgs}. Fire via
 *  {@link grok.events.fireCustomEvent} or the convenience helpers in this module. */
export const MACROMOLECULE_HIGHLIGHT_EVENT_ID = 'bio-macromolecule-monomer-highlight';
/** Slot name on `column.temp` where per-row monomer highlights are stored by the macromolecule
 *  cell renderers. Shape: `Map<rowIdx, MacromoleculeHighlightEntry>`. */
export const MACROMOLECULE_HIGHLIGHT_TEMP = 'bio.macromolecule.highlightMonomers';
/** Forced alpha (0..1) applied to every highlight color — solid colors are not allowed. */
export const MACROMOLECULE_HIGHLIGHT_ALPHA = 0.4;
/** Default fill color (translucent yellow) as a DG.Color ARGB int. The alpha component of the
 *  input is ignored by the renderer and replaced with {@link MACROMOLECULE_HIGHLIGHT_ALPHA}. */
export const DEFAULT_MACROMOLECULE_HIGHLIGHT_FILL = DG.Color.argb(255, 255, 220, 0);
/** Default stroke color (amber) as a DG.Color ARGB int. */
export const DEFAULT_MACROMOLECULE_HIGHLIGHT_STROKE = DG.Color.argb(255, 230, 140, 0);
/** Converts a DG.Color ARGB int to an `rgba(r,g,b,alpha)` CSS string. The input's alpha is
 *  discarded — the fixed {@link MACROMOLECULE_HIGHLIGHT_ALPHA} is used unless an explicit
 *  override is passed (renderers may use a smaller alpha for backgrounds). */
export function macromoleculeHighlightColorToCss(color, alpha) {
    return `rgba(${DG.Color.r(color)},${DG.Color.g(color)},${DG.Color.b(color)},${alpha ?? MACROMOLECULE_HIGHLIGHT_ALPHA})`;
}
function resolveTarget(t) {
    const columnName = t.columnName ?? t.column?.name;
    if (!columnName)
        throw new Error('MacromoleculeHighlight: columnName (or column) must be provided.');
    const tableName = t.tableName ?? t.table?.name;
    const tableId = t.tableId ?? t.table?.id;
    return { tableName, tableId, columnName };
}
/** Fires the macromolecule highlight event. Updates on the renderer side are applied
 *  asynchronously via the platform's custom-event bus; visible cells in the target column will
 *  be repainted. */
export function fireMacromoleculeHighlight(args) {
    grok.events.fireCustomEvent(MACROMOLECULE_HIGHLIGHT_EVENT_ID, args);
}
/** Highlight the given monomers in a single row. Replaces any previous highlight for that row.
 *  Optional `colors` override the defaults. */
export function setMacromoleculeMonomerHighlight(target, rowIdx, monomers, colors) {
    const { tableName, tableId, columnName } = resolveTarget(target);
    fireMacromoleculeHighlight({
        tableName, tableId, columnName, rowIdx, monomers,
        fillColor: colors?.fillColor, strokeColor: colors?.strokeColor,
    });
}
/** Clear the highlight for a single row. */
export function clearMacromoleculeMonomerHighlight(target, rowIdx) {
    const { tableName, tableId, columnName } = resolveTarget(target);
    fireMacromoleculeHighlight({ tableName, tableId, columnName, rowIdx, monomers: null });
}
/** Set highlights for many rows at once. Each value is either `null` (clear that row), an
 *  array of monomer indices (use default colors), or `{monomers, fillColor?, strokeColor?}`. */
export function setMacromoleculeMonomerHighlights(target, rowHighlights) {
    const { tableName, tableId, columnName } = resolveTarget(target);
    const entries = rowHighlights instanceof Map ?
        Array.from(rowHighlights.entries()) :
        Object.entries(rowHighlights).map(([k, v]) => [Number(k), v]);
    for (const [rowIdx, spec] of entries) {
        if (spec == null) {
            fireMacromoleculeHighlight({ tableName, tableId, columnName, rowIdx, monomers: null });
        }
        else if (Array.isArray(spec)) {
            fireMacromoleculeHighlight({ tableName, tableId, columnName, rowIdx, monomers: spec });
        }
        else {
            fireMacromoleculeHighlight({
                tableName, tableId, columnName, rowIdx,
                monomers: spec.monomers, fillColor: spec.fillColor, strokeColor: spec.strokeColor,
            });
        }
    }
}
/** Clears all highlights stored on the column by re-firing a clear event for every currently-
 *  highlighted row. */
export function clearAllMacromoleculeMonomerHighlights(target) {
    const { tableName, tableId, columnName } = resolveTarget(target);
    const col = target.column ?? target.table?.columns.byName(columnName);
    if (!col)
        return;
    const map = col.temp[MACROMOLECULE_HIGHLIGHT_TEMP];
    if (!map || map.size === 0)
        return;
    const rows = Array.from(map.keys());
    for (const rowIdx of rows)
        fireMacromoleculeHighlight({ tableName, tableId, columnName, rowIdx, monomers: null });
}
//# sourceMappingURL=macromolecule-highlight.js.map